import React, { useContext, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Heading,
    Input,
    Select,
    SimpleGrid,
    Text,
    useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useTranslation } from '../i18n';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

/**
 * The other half of the two-field registration.
 *
 * Registration asks for an email and a password; the contact name, the firm
 * name and the headcount are asked here, after the first sign-in. They are
 * not decoration: the firm name is what the product shows in the account menu
 * and prints on exported reports, which is also why registration stores null
 * rather than a placeholder when it does not have them.
 *
 * Shown only to a workspace owner whose account is still missing one of the
 * two names, and dismissable — it is a prompt, not a gate. Nothing about the
 * visitor's clients is asked.
 */
const EMPLOYEE_RANGES = [
    { labelKey: 'profile.employees.solo', value: 1 },
    { labelKey: 'profile.employees.small', value: 2 },
    { labelKey: 'profile.employees.medium', value: 5 },
    { labelKey: 'profile.employees.large', value: 11 },
];

export const needsWorkspaceProfile = (user) => Boolean(
    user && user.role === 'admin' && (!user.name || !user.businessName)
);

const WorkspaceProfilePrompt = () => {
    const { t } = useTranslation();
    const { user, token, refreshUser } = useContext(AuthContext);
    const toast = useToast();
    const [dismissed, setDismissed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        name: user?.name || '',
        businessName: user?.businessName || '',
        numberOfEmployees: 1,
    });

    if (dismissed || !needsWorkspaceProfile(user)) return null;

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: name === 'numberOfEmployees' ? Number(value) : value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const nextErrors = {};
        if (!form.name.trim()) nextErrors.name = t('profile.error.nameRequired');
        if (!form.businessName.trim()) nextErrors.businessName = t('profile.error.businessRequired');
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        setSaving(true);
        try {
            await axios.post(
                `${API_BASE_URL}/auth/complete-profile`,
                {
                    name: form.name.trim(),
                    businessName: form.businessName.trim(),
                    numberOfEmployees: form.numberOfEmployees,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast({ title: t('profile.saved'), status: 'success', duration: 2500, isClosable: true });
            // The account menu and the report header both read these, so the
            // session has to pick them up without a reload.
            await refreshUser().catch(() => {});
            setDismissed(true);
        } catch (error) {
            toast({
                title: t('profile.error'),
                description: error.response?.data?.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box
            as="section"
            aria-label={t('profile.title')}
            bg="white"
            borderWidth="1px"
            borderColor="brand.teal"
            borderRadius="xl"
            boxShadow="b2bCard"
            p={{ base: 5, md: 6 }}
            data-testid="workspace-profile-prompt"
        >
            <Heading size="sm" color="brand.navy">{t('profile.title')}</Heading>
            <Text fontSize="sm" color="brand.muted" mt={1}>{t('profile.body')}</Text>

            <form onSubmit={handleSubmit}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={4}>
                    <FormControl isRequired isInvalid={Boolean(errors.name)}>
                        <FormLabel fontSize="sm">{t('profile.field.name')}</FormLabel>
                        <Input name="name" value={form.name} onChange={handleChange} autoComplete="name" />
                        {errors.name ? <Text fontSize="xs" color="red.600" mt={1}>{errors.name}</Text> : null}
                    </FormControl>
                    <FormControl isRequired isInvalid={Boolean(errors.businessName)}>
                        <FormLabel fontSize="sm">{t('profile.field.business')}</FormLabel>
                        <Input name="businessName" value={form.businessName} onChange={handleChange} autoComplete="organization" />
                        {errors.businessName ? <Text fontSize="xs" color="red.600" mt={1}>{errors.businessName}</Text> : null}
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="sm">{t('profile.field.employees')}</FormLabel>
                        <Select name="numberOfEmployees" value={form.numberOfEmployees} onChange={handleChange}>
                            {EMPLOYEE_RANGES.map((range) => (
                                <option key={range.value} value={range.value}>{t(range.labelKey)}</option>
                            ))}
                        </Select>
                    </FormControl>
                </SimpleGrid>

                <HStack mt={4} spacing={3}>
                    <Button type="submit" colorScheme="teal" size="sm" isLoading={saving}>
                        {t('profile.submit')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDismissed(true)}>
                        {t('profile.later')}
                    </Button>
                </HStack>
            </form>
        </Box>
    );
};

export default WorkspaceProfilePrompt;
